import { confirmDialog } from "../../components/confirmHost.jsx";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Calendar, Clock, Check, X, Trash2, Plane, AlertCircle, Plus, Search, ChevronDown, Users,
} from "lucide-react";
import FiremniVolnoDialog from "../../components/FiremniVolnoDialog.jsx";
import { toast as toastGlobal } from "../../components/Toast.jsx";
import GradientHeader from "../../components/GradientHeader.jsx";
import { DlazdiceFiltr, EmptyState, SkeletonRows, Modal, HlavickaOkna, PruhFiltru, FiltrRadek, Stitek } from "../../components/ui.jsx";
import { apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { formatDateTime } from "../../lib/format.js";
import { MONTH_NAMES } from "../../lib/timeMeta.js";

// HR / Absence - master view se schvalovacím workflow.
// Trenérovy "Plánované volno" požadavky z RozvrhVzor.jsx dorazí sem jako pending.
// Manager (nebo owner) schvaluje nebo zamítá. Pattern výpočet (kapacita) respektuje
// jen approved - pending nemá vliv na detekci pevných klientů.

const REASON_LABEL = { dovolena: "Dovolená", nemoc: "Nemoc", skoleni: "Školení", jine: "Jiné" };
// 7. 10. 2026 (Láďa): karty absencí v designu aplikace (polozka, proužek vlevo, pilulky, iniciály).
const REASON_TONE = {
  dovolena: { pruh: "border-l-sky-400", ton: "info" },
  nemoc:    { pruh: "border-l-rose-400", ton: "problem" },
  skoleni:  { pruh: "border-l-amber-400", ton: "pozor" },
  jine:     { pruh: "border-l-slate-300", ton: "neutralni" },
};
const ROLE_LABEL = { ceo: "CEO", owner: "Owner", manager: "Manažer", "clients manager": "Clients manager", csm: "CSM", "nutricni specialista": "Nutriční specialista", nutritionist: "Nutriční specialista", sales: "Sales", trener: "Trenér" };
const roleText = (r) => ROLE_LABEL[String(r || "").toLowerCase()] || String(r || "");
const inicialy = (j) => String(j || "").split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0].toUpperCase()).join("") || "?";

function fmtCz(iso) {
  if (!iso) return "-";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.${m[1]}` : iso;
}

function fmtCzShort(iso) {
  if (!iso) return "-";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[3], 10)}.${parseInt(m[2], 10)}.` : iso;
}

function dayCount(from, to) {
  if (!from || !to) return 0;
  const d1 = new Date(from + "T00:00:00");
  const d2 = new Date(to + "T00:00:00");
  return Math.round((d2 - d1) / 86400000) + 1;
}

// 6. 10. 2026 (Láďa): schválené a zamítnuté od nejnovější, seskupené po měsících.
// Čekající zůstávají od nejbližšího termínu, ať se schvaluje to, co hoří.
function dnesIso() {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function dniDo(iso, dnes) { return Math.round((new Date(iso + "T00:00:00") - new Date(dnes + "T00:00:00")) / 86400000); }
function nazevMesice(klic) { const [y, m] = klic.split("-").map(Number); const n = MONTH_NAMES[m - 1] || ""; return `${n.charAt(0).toUpperCase()}${n.slice(1)} ${y}`; }
function dniText(n) { return `${n} ${n === 1 ? "den" : n < 5 ? "dny" : "dní"}`; }
function seskupitPoMesicich(seznam) {
  const skupiny = [];
  for (const a of seznam) {
    const klic = String(a.date_from || "").slice(0, 7);
    let g = skupiny[skupiny.length - 1];
    if (!g || g.klic !== klic) { g = { klic, polozky: [], dni: 0 }; skupiny.push(g); }
    g.polozky.push(a); g.dni += dayCount(a.date_from, a.date_to);
  }
  return skupiny;
}

function BulkTrenerVolnoModal({ onClose, onSubmit, submitting, people = [] }) {
  const today = new Date().toISOString().substring(0, 10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [duvod, setDuvod] = useState("dovolena");
  const [poznamka, setPoznamka] = useState("Hromadné firemní volno");
  const [confirm, setConfirm] = useState(false);
  const [err, setErr] = useState(null);
  const [scope, setScope] = useState("trainers"); // trainers | location | all
  const lokality = useMemo(() => [...new Set((people || []).filter((p) => p.is_trener && p.lokalita).map((p) => p.lokalita))].sort((a, b) => a.localeCompare(b, "cs")), [people]);
  const [lokalita, setLokalita] = useState("");
  const REASONS = ["dovolena", "nemoc", "skoleni", "jine"];
  const SCOPE_LABEL = { trainers: "všem aktivním trenérům", location: `trenérům na lokalitě „${lokalita || "?"}"`, all: "celému týmu (trenéři i back office)" };
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={Plane} nazev="Hromadné volno trenérů" onClose={onClose} />}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
          {!confirm ? (
            <button type="button" onClick={() => { if (!dateFrom) { setErr("Vyplň datum od"); return; } if (scope === "location" && !lokalita) { setErr("Vyber lokalitu"); return; } setErr(null); setConfirm(true); }}
              className="btn btn-primary">
              <Plane className="w-4 h-4" /> Pokračovat
            </button>
          ) : (
            <button type="button" disabled={submitting}
              onClick={() => onSubmit({ dateFrom, dateTo: dateTo || dateFrom, duvod, poznamka, scope, lokalita: scope === "location" ? lokalita : "" })}
              className="btn btn-primary">
              {submitting ? <Clock className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Vložit všem
            </button>
          )}
        </>
      }
    >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            Vloží schválené volno na zvolené období (např. vánoční pauza). Kdo už má na to období záznam, se přeskočí.
          </div>
          <div>
            <div className="text-sm text-slate-700 font-medium mb-1.5">Komu</div>
            <div className="grid grid-cols-3 gap-2">
              {[["trainers", "Všem trenérům"], ["location", "Dle lokality"], ["all", "Celý tým"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => { setScope(v); setConfirm(false); }}
                  className={`text-sm font-medium rounded-xl border px-2 py-2 ${scope === v ? "bg-sky-50 border-sky-400 text-sky-700 ring-2 ring-sky-200" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
              ))}
            </div>
            {scope === "location" && (
              <select value={lokalita} onChange={(e) => { setLokalita(e.target.value); setConfirm(false); }}
                className="field mt-2 w-full">
                <option value="">Vyber lokalitu…</option>
                {lokality.map((lo) => <option key={lo} value={lo}>{lo}</option>)}
              </select>
            )}
          </div>
          <div>
            <div className="text-sm text-slate-700 font-medium mb-1.5">Důvod</div>
            <div className="grid grid-cols-4 gap-2">
              {REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setDuvod(r)}
                  className={`text-sm font-medium rounded-xl border px-2 py-2 ${duvod === r ? "bg-sky-50 border-sky-400 text-sky-700 ring-2 ring-sky-200" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                  {REASON_LABEL[r]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 font-medium">Od
              <input type="date" value={dateFrom} min={today} onChange={(e) => setDateFrom(e.target.value)}
                className="field mt-1 w-full" />
            </label>
            <label className="text-sm text-slate-700 font-medium">Do
              <input type="date" value={dateTo} min={dateFrom || today} onChange={(e) => setDateTo(e.target.value)}
                className="field mt-1 w-full" />
            </label>
          </div>
          <label className="block text-sm text-slate-700 font-medium">Poznámka
            <input type="text" value={poznamka} onChange={(e) => setPoznamka(e.target.value)}
              className="field mt-1 w-full" />
          </label>
          {err && <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">{err}</div>}
          {confirm && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-800">
              Opravdu vložit volno {SCOPE_LABEL[scope]} ({REASON_LABEL[duvod]}, {fmtCzShort(dateFrom)}{dateTo && dateTo !== dateFrom ? ` – ${fmtCzShort(dateTo)}` : ""})?
            </div>
          )}
        </div>
    </Modal>
  );
}

export default function HRAbsence() {
  const user = getCurrentUser();
  const userRole = String(user?.role || "").toLowerCase();
  // 21.9.2026: schvaluje jen owner. CSM přidává absence všem, manager trenérům
  // a nutriční, obojí pak čeká na schválení. Hromadné volno dál owner a manager.
  const canApprove = userRole === "owner";
  const canAdd = userRole === "owner" || userRole === "manager" || userRole === "csm";
  const canBulk = userRole === "owner" || userRole === "manager";
  const canManage = canApprove;

  const [absence, setAbsence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [toast, setToast] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // absence object
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [people, setPeople] = useState([]); // pro Add modal - fetched on open
  const [showBulk, setShowBulk] = useState(false);
  // 3. 11. 2026: Firemní volno (celá firma) i tady, navrhuje manager a CSM, schvaluje owner.
  const canFiremni = ["owner", "manager", "csm"].includes(userRole);
  const [showFiremni, setShowFiremni] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/api/hr-absence", { credentials: "include" });
      const j = await r.json();
      if (j.ok) setAbsence(j.absence || []);
      else showToast("err", j.error || "Načtení selhalo");
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Lazy-load lidí pro Add modal - až když manager otevře dialog.
  const loadPeopleIfNeeded = useCallback(async () => {
    if (people.length > 0) return;
    try {
      const r = await apiFetch("/api/hr-list-people?filter=vse&status=aktivni", { credentials: "include" });
      const j = await r.json();
      if (j.ok) setPeople(j.people || []);
    } catch (e) {
      showToast("err", `Načtení lidí selhalo: ${e.message}`);
    }
  }, [people.length, showToast]);

  const handleCreate = useCallback(async (payload) => {
    setWorking(true);
    try {
      const r = await apiFetch("/api/hr-absence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Vytvoření selhalo");
      showToast("ok", j.status === "pending" ? "Odesláno ke schválení ownerovi" : "Absence přidána");
      setShowAddModal(false);
      await loadAll();
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setWorking(false);
    }
  }, [loadAll, showToast]);

  const handleBulk = useCallback(async (payload) => {
    setBulkBusy(true);
    try {
      const r = await apiFetch("/api/hr-absence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action: "bulkAll", ...payload }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Hromadné volno selhalo");
      setShowBulk(false);
      showToast("ok", `Volno vloženo ${j.inserted} trenérům`);
      await loadAll();
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  }, [loadAll, showToast]);

  const dnes = dnesIso();
  const [rokFiltr, setRokFiltr] = useState([]);
  const [roleFiltr, setRoleFiltr] = useState([]);
  const roky = useMemo(() => [...new Set(absence.filter((a) => a.status === activeTab).map((a) => String(a.date_from || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [absence, activeTab]);
  const role = useMemo(() => [...new Set(absence.map((a) => a.person_role).filter(Boolean))].sort((a, b) => a.localeCompare(b, "cs")), [absence]);
  const filtered = useMemo(() => {
    const dir = activeTab === "pending" ? 1 : -1;
    return absence
      .filter((a) => a.status === activeTab)
      .filter((a) => !rokFiltr.length || rokFiltr.includes(String(a.date_from || "").slice(0, 4)))
      .filter((a) => !roleFiltr.length || roleFiltr.includes(a.person_role))
      .sort((a, b) => dir * (String(a.date_from).localeCompare(String(b.date_from)) || (Number(a.id) - Number(b.id))));
  }, [absence, activeTab, rokFiltr, roleFiltr]);
  const skupiny = useMemo(() => (activeTab === "pending" ? null : seskupitPoMesicich(filtered)), [filtered, activeTab]);
  // Aktuální a budoucí měsíce rozbalené, starší sbalené, klik přepne (7. 10. 2026, Láďa).
  const [prepnute, setPrepnute] = useState(() => new Set());
  const jeRozbaleny = (klic) => { const vychozi = klic >= dnes.slice(0, 7); return prepnute.has(klic) ? !vychozi : vychozi; };
  const prepni = (klic) => setPrepnute((r) => { const n = new Set(r); if (n.has(klic)) n.delete(klic); else n.add(klic); return n; });
  const celkemDni = useMemo(() => filtered.reduce((n, a) => n + dayCount(a.date_from, a.date_to), 0), [filtered]);

  const counts = useMemo(() => ({
    pending: absence.filter((a) => a.status === "pending").length,
    approved: absence.filter((a) => a.status === "approved").length,
    rejected: absence.filter((a) => a.status === "rejected").length,
  }), [absence]);

  const handleApprove = useCallback(async (id) => {
    if (!canManage) return;
    setWorking(true);
    try {
      const r = await apiFetch("/api/hr-absence?action=approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Schválení selhalo");
      showToast("ok", "Absence schválena");
      await loadAll();
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setWorking(false);
    }
  }, [canManage, loadAll, showToast]);

  const handleReject = useCallback(async () => {
    if (!canManage || !rejectFor) return;
    if (!rejectReason.trim()) {
      showToast("err", "Důvod zamítnutí je povinný");
      return;
    }
    setWorking(true);
    try {
      const r = await apiFetch("/api/hr-absence?action=reject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id: rejectFor.id, reason: rejectReason.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Zamítnutí selhalo");
      showToast("ok", "Absence zamítnuta");
      setRejectFor(null);
      setRejectReason("");
      await loadAll();
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setWorking(false);
    }
  }, [canManage, rejectFor, rejectReason, loadAll, showToast]);

  const handleDelete = useCallback(async (id) => {
    if (!await confirmDialog("Opravdu smazat tuto absenci?")) return;
    setWorking(true);
    try {
      const r = await apiFetch("/api/hr-absence", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Smazání selhalo");
      showToast("ok", "Smazáno");
      await loadAll();
    } catch (e) {
      showToast("err", `Chyba: ${e.message}`);
    } finally {
      setWorking(false);
    }
  }, [loadAll, showToast]);

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* Hlavička */}
      <GradientHeader
        title="Absence"
        subtitle="Plánované volno týmu. Schvaluje owner, kapacita počítá jen schválené."
        icon={Plane}
        akce={canAdd ? [
          { klic: "pridat", ikona: Plus, popis: "Přidat absenci", onClick: () => { loadPeopleIfNeeded(); setShowAddModal(true); } },
          ...(canFiremni ? [{ klic: "firemni", ikona: Plane, popis: "Firemní volno (celá firma)", onClick: () => setShowFiremni(true) }] : []),
          ...(canBulk ? [{ klic: "hromadne", ikona: Users, popis: "Hromadná dovolená trenérům (čerpá dovolenou)", onClick: () => setShowBulk(true) }] : []),
        ] : undefined}
      />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-2xl text-base font-semibold border ${
          toast.type === "ok"
            ? "bg-emerald-50 text-emerald-800 border-emerald-300"
            : "bg-rose-50 text-rose-800 border-rose-300"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* 12.9.2026 (fáze 4c): bubliny z tmavé hlavičky a záložky nad seznamem
          byly totéž číslo dvakrát. Teď je to jedna řada dlaždic, která zároveň
          filtruje (rozhodnutí Č2). */}
      <div className="grid grid-cols-3 gap-3">
        <DlazdiceFiltr
          popisek="Čekající"
          hodnota={counts.pending}
          ikona={Clock}
          ton="pozor"
          zapnuto={activeTab === "pending"}
          onClick={() => setActiveTab("pending")}
        />
        <DlazdiceFiltr
          popisek="Schváleno"
          hodnota={counts.approved}
          ikona={Check}
          ton="ok"
          zapnuto={activeTab === "approved"}
          onClick={() => setActiveTab("approved")}
        />
        <DlazdiceFiltr
          popisek="Zamítnuto"
          hodnota={counts.rejected}
          ikona={X}
          ton="neutralni"
          zapnuto={activeTab === "rejected"}
          onClick={() => setActiveTab("rejected")}
        />
      </div>

      <div className="glass overflow-hidden rounded-2xl">

        {/* List */}
        <div className="p-4 space-y-2">
          {loading && <SkeletonRows rows={4} />}
          {!loading && filtered.length === 0 && (
            <EmptyState
              icon={Calendar}
              title={
                activeTab === "pending" ? "Nic nečeká na schválení"
                  : activeTab === "approved" ? "Žádné schválené absence"
                  : "Žádné zamítnuté absence"
              }
              hint="Přepni dlaždici nahoře a uvidíš ostatní stavy."
            />
          )}
          {!loading && (roky.length > 1 || role.length > 1) && (
            <PruhFiltru>
              <div className="grid gap-2 sm:grid-cols-2">
                {roky.length > 1 && <FiltrRadek stitek="Rok" items={roky.map((r) => ({ key: r, label: r }))} value={rokFiltr} onChange={setRokFiltr} />}
                {role.length > 1 && <FiltrRadek stitek="Role" popisVse="Všichni" items={role.map((r) => ({ key: r, label: roleText(r) }))} value={roleFiltr} onChange={setRoleFiltr} />}
              </div>
              <div className="px-1 text-[12px] text-slate-500">Zobrazeno <b className="text-slate-700">{filtered.length} {filtered.length === 1 ? "absence" : filtered.length < 5 ? "absence" : "absencí"}</b>, celkem {dniText(celkemDni)}</div>
            </PruhFiltru>
          )}
          {!loading && !skupiny && filtered.map((a) => (
            <AbsenceRow
              key={a.id}
              absence={a}
              dnes={dnes}
              canManage={canManage}
              mujEmail={String(user?.email || "").toLowerCase()}
              userRole={userRole}
              working={working}
              onApprove={() => handleApprove(a.id)}
              onReject={() => { setRejectFor(a); setRejectReason(""); }}
              onDelete={() => handleDelete(a.id)}
            />
          ))}
          {!loading && skupiny && skupiny.map((g) => {
            const otevreno = jeRozbaleny(g.klic);
            const budouci = g.klic > dnes.slice(0, 7);
            const tento = g.klic === dnes.slice(0, 7);
            return (
              <div key={g.klic} className="space-y-2">
                <button type="button" onClick={() => prepni(g.klic)} aria-expanded={otevreno}
                  className="polozka flex min-h-[52px] w-full flex-wrap items-center gap-2 text-left sm:gap-3">
                  <span className="whitespace-nowrap font-serif text-[17px] font-bold text-brand-900">{nazevMesice(g.klic)}</span>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-100 px-2.5 py-1 text-[13px] font-bold text-brand-deep">{g.polozky.length} {g.polozky.length === 1 ? "absence" : g.polozky.length < 5 ? "absence" : "absencí"}<span className="h-3 w-px bg-brand-300" aria-hidden="true" />{dniText(g.polozky.reduce((n, a) => n + dayCount(a.date_from, a.date_to), 0))}</span>
                  {tento && <Stitek ton="info" velikost="mala">tento měsíc</Stitek>}
                  {budouci && <Stitek ton="info" velikost="mala">nadcházející</Stitek>}
                  <ChevronDown className={`ml-auto h-5 w-5 shrink-0 transition-transform ${otevreno ? "rotate-180 text-brand" : "text-slate-400"}`} />
                </button>
                {otevreno && g.polozky.map((a) => (
                  <AbsenceRow
                    key={a.id}
                    absence={a}
                    dnes={dnes}
                    canManage={canManage}
                    mujEmail={String(user?.email || "").toLowerCase()}
                    userRole={userRole}
                    working={working}
                    onApprove={() => handleApprove(a.id)}
                    onReject={() => { setRejectFor(a); setRejectReason(""); }}
                    onDelete={() => handleDelete(a.id)}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Reject modal */}
      {rejectFor && (
        <RejectModal
          absence={rejectFor}
          reason={rejectReason}
          setReason={setRejectReason}
          working={working}
          onCancel={() => { setRejectFor(null); setRejectReason(""); }}
          onConfirm={handleReject}
        />
      )}

      {/* Add modal - manager přidává absenci komukoli (status=approved okamžitě) */}
      {showAddModal && (
        <AddAbsenceModal
          people={people}
          userRole={userRole}
          working={working}
          onCancel={() => setShowAddModal(false)}
          onConfirm={handleCreate}
        />
      )}
      <FiremniVolnoDialog open={showFiremni} onClose={() => setShowFiremni(false)}
        onHotovo={async (j) => {
          if (!j || j.stav !== "schvaleno") { toastGlobal("Firemní volno je odeslané ownerovi ke schválení.", "success"); return; }
          // Provedení po krocích (úkoly, bootcamp, SMS, platnost): když server nestihne všechno, pokračuje se.
          let x = j;
          try {
            for (let i = 0; i < 8 && x && x.hotovo === false; i++) {
              const r = await apiFetch("/api/firemni-volno", { method: "POST", body: JSON.stringify({ akce: "dokoncit", id: j.id }) });
              x = await r.json().catch(() => ({}));
              if (!r.ok || !x.ok) throw new Error(x.error || "Dokončení se nepovedlo.");
            }
            toastGlobal("Firemní volno je schválené a provedené.", "success");
          } catch (e) {
            toastGlobal(`Firemní volno je schválené, ale neprovedlo se celé (${e.message}). Dokonči ho v Klienti → Dovolené klientů.`, "error");
          }
        }} />
      {showBulk && (
        <BulkTrenerVolnoModal
          submitting={bulkBusy}
          people={people}
          onClose={() => setShowBulk(false)}
          onSubmit={handleBulk}
        />
      )}
    </div>
  );
}

function AbsenceRow({ absence, dnes, canManage, mujEmail, userRole, working, onApprove, onReject, onDelete }) {
  const a = absence;
  const tone = REASON_TONE[a.duvod] || REASON_TONE.jine;
  const label = REASON_LABEL[a.duvod] || a.duvod;
  const days = dayCount(a.date_from, a.date_to);
  const isMulti = a.date_from !== a.date_to;
  // Proběhlá absence je ztlumená, běžící a nadcházející mají štítek.
  const minula = !!dnes && a.date_to < dnes;
  const probiha = !!dnes && a.date_from <= dnes && a.date_to >= dnes;
  const zaDni = dnes && a.date_from > dnes ? dniDo(a.date_from, dnes) : null;

  return (
    <div className={`polozka flex items-start gap-3 border-l-[3px] ${tone.pruh} ${minula && a.status !== "pending" ? "opacity-70" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-bold text-brand-700">{inicialy(a.person_display)}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[15px] font-bold text-slate-900 demo-sensitive">{a.person_display}</span>
          <Stitek ton={tone.ton} velikost="mala">{label}</Stitek>
          {a.person_role && <Stitek ton="neutralni" velikost="mala">{roleText(a.person_role)}</Stitek>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[14px] font-semibold text-slate-700">
          <span>{isMulti ? `${fmtCz(a.date_from)} – ${fmtCz(a.date_to)}` : fmtCz(a.date_from)}</span>
          {a.time_from && a.time_to
            ? <Stitek ton="info" velikost="mala">{a.time_from}–{a.time_to}</Stitek>
            : <Stitek ton="neutralni" velikost="mala">{days} {days === 1 ? "den" : days < 5 ? "dny" : "dní"}</Stitek>}
          {probiha && <Stitek ton="ok" velikost="mala">probíhá</Stitek>}
          {zaDni != null && zaDni <= 60 && <Stitek ton="info" velikost="mala">{zaDni === 0 ? "dnes" : zaDni === 1 ? "zítra" : `za ${dniText(zaDni)}`}</Stitek>}
        </div>
        {a.poznamka && <div className="mt-1 text-[13px] text-slate-600">„{a.poznamka}“</div>}
        {(a.created_by || (a.status === "approved" && a.approved_by)) && (
          <div className="mt-1 text-[12px] text-slate-400">
            {a.created_by && <>Zadal: {String(a.created_by).includes("@") ? String(a.created_by).split("@")[0] : a.created_by}</>}
            {a.created_by && a.status === "approved" && a.approved_by && <span className="mx-1.5">·</span>}
            {a.status === "approved" && a.approved_by && <>Schválil: {String(a.approved_by).includes("@") ? String(a.approved_by).split("@")[0] : a.approved_by}{a.approved_at && `, ${formatDateTime(a.approved_at)}`}</>}
          </div>
        )}
        {a.status === "rejected" && (
          <div className="mt-1 text-[12px] text-rose-600">
            <strong>Zamítnuto:</strong> {a.rejected_reason || "(bez důvodu)"}
            {a.rejected_by && `, zamítl ${a.rejected_by}`}
          </div>
        )}
      </div>

      {/* Akce */}
      <div className="flex items-center gap-2 shrink-0">
        {canManage && a.status === "pending" && (
          <>
            <button
              onClick={onApprove}
              disabled={working}
              className="btn btn-primary"
            >
              <Check className="w-4 h-4" /> Schválit
            </button>
            <button
              onClick={onReject}
              disabled={working}
              className="btn btn-danger"
            >
              <X className="w-4 h-4" /> Zamítnout
            </button>
          </>
        )}
        {(canManage || userRole === "manager"
          || (userRole === "csm" && a.status === "pending" && String(a.created_by || "").toLowerCase() === mujEmail)) && (
          <button
            onClick={onDelete}
            disabled={working}
            title="Smazat"
            className="btn-ikona text-rose-600"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

const REASON_OPTIONS = [
  { value: "dovolena", label: "Dovolená", emoji: "🏖️" },
  { value: "nemoc",    label: "Nemoc",    emoji: "🤒" },
  { value: "skoleni",  label: "Školení",  emoji: "📚" },
  { value: "jine",     label: "Jiné",     emoji: "📌" },
];

function AddAbsenceModal({ people, userRole, working, onCancel, onConfirm }) {
  const jeOwner = userRole === "owner";
  const today = new Date().toISOString().substring(0, 10);
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null); // { id, display_name, is_trener, pozice }
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [duvod, setDuvod] = useState("dovolena");
  const [poznamka, setPoznamka] = useState("");
  const [celyDen, setCelyDen] = useState(true);        // hodinové volno jen pro jeden den
  const [timeFrom, setTimeFrom] = useState("09:00");
  const [timeTo, setTimeTo] = useState("12:00");
  const jedenDen = !!dateFrom && dateFrom === dateTo;

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Manager přidává jen trenérům a nutriční (21.9.2026), backend to hlídá taky.
    const mozni = userRole === "manager"
      ? people.filter((p) => p.is_trener || /nutri|výživ|vyziv/i.test(`${p.pozice || ""} ${p.role || ""}`))
      : people;
    const sorted = [...mozni].sort((a, b) => {
      // trenéři první, pak abecedně
      if (a.is_trener && !b.is_trener) return -1;
      if (!a.is_trener && b.is_trener) return 1;
      return (a.display_name || "").localeCompare(b.display_name || "", "cs");
    });
    if (!q) return sorted;
    return sorted.filter((p) =>
      (p.display_name || "").toLowerCase().includes(q) ||
      (p.pozice || "").toLowerCase().includes(q)
    );
  }, [people, search, userRole]);

  const days = useMemo(() => {
    if (!dateFrom || !dateTo || dateTo < dateFrom) return 0;
    return Math.round((new Date(dateTo + "T00:00:00") - new Date(dateFrom + "T00:00:00")) / 86400000) + 1;
  }, [dateFrom, dateTo]);

  const errors = useMemo(() => {
    const e = {};
    if (!picked) e.person = "Vyber osobu";
    if (dateTo < dateFrom) e.dateTo = "Datum do musí být ≥ od";
    if (jedenDen && !celyDen && timeTo <= timeFrom) e.time = "Konec musí být po začátku";
    return e;
  }, [picked, dateFrom, dateTo, jedenDen, celyDen, timeFrom, timeTo]);
  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = () => {
    if (!isValid || !picked) return;
    onConfirm({
      personId: picked.id || null,
      personDisplay: picked.display_name,
      personRole: picked.is_trener ? "trener" : (picked.pozice || "ostatni").toLowerCase(),
      dateFrom,
      dateTo,
      duvod,
      poznamka: poznamka.trim(),
      ...(jedenDen && !celyDen ? { timeFrom, timeTo } : {}),
    });
  };

  return (
    <Modal
      open
      onClose={onCancel}
      size="md"
      plnaHlavicka
      header={<HlavickaOkna ikona={Plane} nazev="Přidat absenci" popis={jeOwner ? "Schválí se rovnou. Tréninky v období se nezapočítají do kapacity." : "Uloží se jako čekající, schválí ji owner. Tréninky v období se pak nezapočítají do kapacity."} onClose={onCancel} />}
      footer={
        <>
          <button onClick={onCancel} disabled={working} className="btn btn-ghost">Zrušit</button>
          <button onClick={handleSubmit} disabled={working || !isValid} className="btn btn-primary">
            <Check className="w-4 h-4" /> {jeOwner ? "Uložit" : "Odeslat ke schválení"}
          </button>
        </>
      }
    >
        <div className="space-y-4">
          {/* Výběr osoby */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Osoba *</label>
            {picked ? (
              <div className="flex items-center justify-between gap-2 p-3 bg-sky-50 border-2 border-sky-300 rounded-xl">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {picked.display_name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-800 truncate">{picked.display_name}</div>
                    <div className="text-xs text-slate-500">
                      {picked.is_trener ? "Trenér" : (picked.pozice || "-")}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setPicked(null)}
                  className="text-slate-500 hover:text-slate-700 px-2 py-1 text-sm font-bold"
                >
                  Změnit
                </button>
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Hledat jméno nebo pozici…"
                    className="field w-full pl-9 pr-3"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {filteredPeople.length === 0 && (
                    <div className="p-3 text-sm text-slate-500 italic text-center">
                      {people.length === 0 ? "Načítám…" : "Žádná osoba neodpovídá."}
                    </div>
                  )}
                  {filteredPeople.slice(0, 50).map((p) => (
                    <button
                      key={p.id || p.display_name}
                      onClick={() => setPicked(p)}
                      className="w-full text-left px-3 py-2 hover:bg-sky-50 flex items-center gap-2"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {p.display_name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-slate-800 truncate">{p.display_name}</div>
                        <div className="text-xs text-slate-500">
                          {p.is_trener ? "Trenér" : (p.pozice || "-")}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {errors.person && <div className="text-xs text-rose-600 mt-1">{errors.person}</div>}
              </>
            )}
          </div>

          {/* Důvod */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-2 block">Důvod</label>
            <div className="grid grid-cols-4 gap-2">
              {REASON_OPTIONS.map((opt) => {
                const active = duvod === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDuvod(opt.value)}
                    className={`px-2 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                      active
                        ? "bg-sky-600 border-sky-600 text-white shadow-md scale-105"
                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-lg mb-0.5">{opt.emoji}</div>
                    <div>{opt.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Datumy */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Od</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); if (e.target.value > dateTo) setDateTo(e.target.value); }}
                className="field w-full"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Do</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom}
                onChange={(e) => setDateTo(e.target.value)}
                className={`w-full px-3 py-2.5 text-base border-2 rounded-xl outline-none transition-colors ${
                  errors.dateTo ? "border-rose-300 bg-rose-50" : "border-slate-200 focus:border-sky-500"
                }`}
              />
              {errors.dateTo && <div className="text-xs text-rose-600 mt-1">{errors.dateTo}</div>}
            </div>
          </div>

          {days > 0 && (
            <div className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2 text-sm text-sky-800">
              <span className="font-bold">{days}</span> {days === 1 ? "den" : days < 5 ? "dny" : "dní"} volna
            </div>
          )}

          {/* Hodinové (částečné) volno — jen pro jednodenní absenci (zubař, kino apod.) */}
          {jedenDen && (
            <div>
              <div className="inline-flex rounded-xl border-2 border-slate-200 overflow-hidden mb-2">
                <button type="button" onClick={() => setCelyDen(true)}
                  className={`px-3 py-2 text-sm font-bold transition-colors ${celyDen ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Celý den</button>
                <button type="button" onClick={() => setCelyDen(false)}
                  className={`px-3 py-2 text-sm font-bold transition-colors ${!celyDen ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Jen část dne</button>
              </div>
              {!celyDen && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Od (hodina)</label>
                    <input type="time" value={timeFrom} onChange={(e) => setTimeFrom(e.target.value)}
                      className="field w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">Do (hodina)</label>
                    <input type="time" value={timeTo} onChange={(e) => setTimeTo(e.target.value)}
                      className={`w-full px-3 py-2.5 text-base border-2 rounded-xl outline-none transition-colors ${errors.time ? "border-rose-300 bg-rose-50" : "border-slate-200 focus:border-sky-500"}`} />
                    {errors.time && <div className="text-xs text-rose-600 mt-1">{errors.time}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Poznámka */}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">
              Poznámka <span className="text-slate-400 normal-case font-normal">(volitelné)</span>
            </label>
            <input
              type="text"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              placeholder="Např. dovolená v Itálii, lékař, externí workshop"
              className="field w-full"
            />
          </div>

          {/* Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <span className="text-base">🔔</span>
            <div>Absence přidaná manažerem se rovnou schválí a odečte se z rozvrhu.</div>
          </div>
        </div>

    </Modal>
  );
}

function RejectModal({ absence, reason, setReason, working, onCancel, onConfirm }) {
  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      plnaHlavicka
      header={
        <HlavickaOkna
          ikona={AlertCircle}
          nazev="Zamítnout absenci"
          popis={`${absence.person_display}, ${REASON_LABEL[absence.duvod] || absence.duvod}, ${absence.date_from === absence.date_to ? fmtCz(absence.date_from) : `${fmtCzShort(absence.date_from)} – ${fmtCz(absence.date_to)}`}`}
          onClose={onCancel}
        />
      }
      footer={
        <>
          <button onClick={onCancel} disabled={working} className="btn btn-ghost">Zrušit</button>
          <button onClick={onConfirm} disabled={working || !reason.trim()} className="btn btn-danger">Zamítnout</button>
        </>
      }
    >
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-600 font-bold mb-1 block">
              Důvod zamítnutí <span className="text-rose-600">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Např. termín koliduje s plánovaným školením celého týmu"
              rows={3}
              className="field w-full"
            />
            <div className="text-xs text-slate-500 mt-1">
              Trenér uvidí důvod, ať ví proč.
            </div>
          </div>
        </div>
    </Modal>
  );
}
